import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { useMembershipMode } from '@/hooks/useMembershipMode';
import { OrganizationService } from '@/services/organizationService';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';

export interface AccessState {
  hasAccess: boolean;
  isLoading: boolean;
  accessType: 'granted' | 'pending' | 'denied' | 'invite_only' | 'unauthenticated';
  pendingMembership?: any;
  organization?: any;
  membership?: any;
  membershipSettings?: any;
}

export const useEnhancedOrganizationAccess = (): AccessState => {
  const { user } = useAuth();
  const { organization, isLoading: orgLoading } = useOrganization();
  const { settings: membershipSettings, loading: settingsLoading } = useMembershipMode();
  const queryClient = useQueryClient();

  // Check existing membership
  const { data: membership, isLoading: membershipLoading } = useQuery({
    queryKey: ['organization-membership', user?.id, organization?.id],
    queryFn: async () => {
      if (!user?.id || !organization?.id) return null;
      
      // Get user profile ID first
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();
      
      if (!profile) return null;
      return await OrganizationService.fetchMembership(profile.id, organization.id);
    },
    enabled: !!user?.id && !!organization?.id,
  });

  // Check pending membership
  const { data: pendingMembership, isLoading: pendingLoading } = useQuery({
    queryKey: ['pending-membership', user?.id, organization?.id],
    queryFn: async () => {
      if (!user?.id || !organization?.id || membership) return null;
      
      // Get user profile ID first
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();
      
      if (!profile) return null;
      
      const { data, error } = await supabase
        .from('pending_memberships')
        .select('*')
        .eq('user_id', profile.id)
        .eq('org_id', organization.id)
        .eq('status', 'pending')
        .maybeSingle();
        
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !!organization?.id && !membership && !membershipLoading,
  });

  const isLoading = orgLoading || membershipLoading || pendingLoading || settingsLoading;

  // Determine access state
  if (!user) {
    return {
      hasAccess: true, // Allow unauthenticated users to see the app (auth will handle redirects)
      isLoading,
      accessType: 'unauthenticated',
      organization,
      membershipSettings,
    };
  }

  if (isLoading) {
    return {
      hasAccess: false,
      isLoading: true,
      accessType: 'denied',
      organization,
      membershipSettings,
    };
  }

  // User has active membership
  if (OrganizationService.hasOrganizationAccess(membership)) {
    return {
      hasAccess: true,
      isLoading: false,
      accessType: 'granted',
      organization,
      membership,
      membershipSettings,
    };
  }

  // User has pending membership
  if (pendingMembership) {
    return {
      hasAccess: false,
      isLoading: false,
      accessType: 'pending',
      pendingMembership,
      organization,
      membershipSettings,
    };
  }

  // Check membership mode and auto-create membership for open organizations
  if (membershipSettings.membershipMode === 'open') {
    // Auto-create membership in the background
    if (user?.id && organization?.id) {
      (async () => {
        try {
          // Get user profile ID
          const { data: profile } = await supabase
            .from('profiles')
            .select('id')
            .eq('auth_user_id', user.id)
            .single();
          
          if (!profile) return;

          // Check if membership already exists to avoid duplicates
          const { data: existingRole } = await supabase
            .from('organization_user_roles')
            .select('id')
            .eq('user_id', profile.id)
            .eq('org_id', organization.id)
            .maybeSingle();

          if (!existingRole) {
            // Create the membership with the default role
            const { error } = await supabase
              .from('organization_user_roles')
              .insert({
                user_id: profile.id,
                org_id: organization.id,
                role: membershipSettings.defaultMemberRole || 'member',
              });

            if (!error) {
              // Invalidate queries to refetch with new membership
              queryClient.invalidateQueries({ queryKey: ['organization-membership'] });
            }
          }
        } catch (error) {
          console.error('Error auto-creating membership:', error);
        }
      })();
    }

    return {
      hasAccess: true,
      isLoading: false,
      accessType: 'granted',
      organization,
      membershipSettings,
    };
  }

  // User needs access - determine type based on membership mode
  const accessType = membershipSettings.membershipMode === 'invite_only' ? 'invite_only' : 'denied';
  
  return {
    hasAccess: false,
    isLoading: false,
    accessType,
    organization,
    membershipSettings,
  };
};